'use client';

import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';

import type { ChatAvatarProps } from '@/components/ui/chat-avatar';

interface Message {
  id: string;
  content: ReactNode;
  avatar?: ChatAvatarProps;
  timestamp: Date;
}

export interface IChatContext {
  messages: Array<Message>;
  postMessage: (message: Message) => void;
  error: string | null;
  setError: (error: string | null) => void;
  clearMessages: () => void;
}

const ChatContext = createContext<IChatContext>({
  messages: [],
  postMessage: (_message: Message) => {
    console.warn('ChatContext used outside of provider');
  },
  clearMessages: () => {
    console.warn('ChatContext used outside of provider');
  },
  error: null,
  setError: (_error: string | null) => {
    console.warn('ChatContext used outside of provider');
  },
});

interface ChatProviderProps {
  children: ReactNode;
  messages?: Array<Message>;
}
export const ChatProvider = ({
  children,
  messages: _messages,
}: ChatProviderProps) => {
  const [messages, setMessages] = useState<Array<Message>>(_messages ?? []);
  const [error, setError] = useState<string | null>(null);

  const postMessage = useCallback((message: Message) => {
    setMessages((prev) => [...prev, message]);
  }, []);

  const clearMessages = useCallback(() => {
    setMessages([]);
  }, []);

  const value = useMemo(
    () => ({ messages, error, setError, postMessage, clearMessages }),
    [messages, error, postMessage, clearMessages]
  );

  return <ChatContext.Provider value={value}>{children}</ChatContext.Provider>;
};

export const useChat = () => {
  const context = useContext(ChatContext);
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  if (!context) {
    throw new Error('useChat must be used within a ChatProvider');
  }
  return context;
};
